import { getCopy } from '../../utils/copy';
import { useState } from 'react';

/**
 * 图解数学 · 鸡兔同笼(迈腿出笼法)
 *
 * 孩子自己点按钮一步步推:
 *   0 笼子里 heads 只动物、legs 只脚,看不出谁是鸡谁是兔
 *   1 每只都往笼外迈一条腿        → 笼子里还剩 legs - heads 只脚
 *   2 再迈一条腿:鸡只有 2 条腿,整只走出笼子;兔子卡在笼门口,
 *     还有 2 只脚在笼子里        → 笼子里还剩 legs - 2×heads 只脚,全是兔子的
 *   3 每只兔子笼里剩 2 只脚,除以 2 → 兔子数;总数减兔子 → 鸡数,顺带验算
 *
 * 身份(鸡/兔)在第 2 步才揭晓 —— 那一刻正是这个方法想让孩子自己看出来的:
 * 走出去的是鸡,留在笼里的是兔。在那之前笼底铺着稻草,看不到笼里每只有几只脚,
 * 不然直接数腿就等于把答案摆出来了。
 *
 * 画面是多层鸡笼:每层上半是木栏笼子,中间一道笼门横梁,下半是笼外草地。
 * 整个讲解要在一屏手机里放下,所以只显示当前这一步的说明,前面的算式缩成一排小标签。
 *
 * heads / legs 是参数:同一个组件能讲任意一道鸡兔同笼题(规划见
 * docs/plan-002-tujie-math-liuyi.md)。
 */

/** 解鸡兔同笼;数字不构成合法的鸡兔同笼题时返回 null */
export function solveChickenRabbit(heads, legs) {
  if (!Number.isInteger(heads) || !Number.isInteger(legs)) return null;
  if (heads <= 0 || legs % 2 !== 0) return null;
  if (legs < 2 * heads || legs > 4 * heads) return null;
  const rabbits = (legs - 2 * heads) / 2;
  return { rabbits, chickens: heads - rabbits };
}

/** 把兔子均匀撒在笼子里,而不是全挤在最后几格(确定性,测试可复现) */
function isRabbitAt(index, heads, rabbits) {
  return Math.floor(((index + 1) * rabbits) / heads) > Math.floor((index * rabbits) / heads);
}

const LAST_STEP = 3;
const PER_TIER = 10; // 每层笼子放几只

// 每只动物占一列,列内按像素定位:横梁以上是笼内,以下是笼外草地
const COL_W = 30;
const CAGE_H = 48;
const BEAM_H = 5;
const GRASS_H = 46;
const LEG_H = 12;

const STEP_BUTTONS = ['大家往笼外迈一条腿!', '再迈一条腿!', '笼里剩的脚,除以 2'];

function Leg({ out }) {
  return (
    <span
      data-testid={out ? 'leg-out' : 'leg-in'}
      className={`inline-block w-1 rounded-full ${out ? 'bg-amber-700' : 'bg-gray-900'}`}
      style={{ height: LEG_H }}
    />
  );
}

function Animal({ isRabbit, step }) {
  const revealed = step >= 2;
  const walkedOut = revealed && !isRabbit; // 鸡两条腿都迈出去 = 整只出笼
  const legsOut = Math.min(step, 2);
  const legsIn = revealed ? (isRabbit ? 2 : 0) : null; // 揭晓前笼底有稻草,看不见

  let face = '❓';
  if (revealed) face = isRabbit ? '🐰' : '🐔';
  const highlight = step === LAST_STEP && isRabbit;

  return (
    <div className="relative shrink-0" style={{ width: COL_W, height: CAGE_H + BEAM_H + GRASS_H }}>
      {/* 身体:在笼里时贴着横梁上方;鸡走出去后移到草地上 */}
      <span
        data-testid={revealed ? (isRabbit ? 'animal-rabbit' : 'animal-chicken') : 'animal-unknown'}
        className={`absolute left-1/2 flex h-7 w-7 -translate-x-1/2 items-center justify-center rounded-full
                    text-xl leading-none transition-all duration-700 ease-out
                    ${highlight ? 'bg-qiji-mint ring-2 ring-black' : ''}`}
        style={{ top: walkedOut ? CAGE_H + BEAM_H + 2 : 3 }}
      >
        {face}
      </span>

      {/* 还在笼里的脚:横梁上方 */}
      <div
        className="absolute inset-x-0 flex justify-center gap-0.5"
        style={{ top: CAGE_H - LEG_H - 2 }}
      >
        {legsIn === null ? (
          <span className="w-6 rounded-sm bg-amber-300" style={{ height: LEG_H }} title="稻草" />
        ) : (
          Array.from({ length: legsIn }, (_, i) => <Leg key={i} />)
        )}
      </div>

      {/* 迈出去的脚:横梁下方的草地里(鸡整只出笼后,脚跟着身子走) */}
      <div
        className="absolute inset-x-0 flex justify-center gap-0.5 transition-all duration-700 ease-out"
        style={{ top: walkedOut ? CAGE_H + BEAM_H + 31 : CAGE_H + BEAM_H + 1 }}
      >
        {Array.from({ length: legsOut }, (_, i) => (
          <Leg key={i} out />
        ))}
      </div>
    </div>
  );
}

function CageTier({ indices, heads, rabbits, step }) {
  return (
    <div className="relative overflow-hidden rounded border-2 border-amber-900">
      {/* 笼内:木栏杆 */}
      <div
        className="absolute inset-x-0 top-0"
        style={{
          height: CAGE_H,
          background: 'repeating-linear-gradient(90deg, #fef3c7 0 12px, #92400e 12px 15px)',
        }}
      />
      {/* 笼门横梁 */}
      <div className="absolute inset-x-0 bg-amber-900" style={{ top: CAGE_H, height: BEAM_H }} />
      {/* 笼外:草地 */}
      <div className="absolute inset-x-0 bottom-0 bg-green-200" style={{ height: GRASS_H }} />
      <div className="relative flex justify-center">
        {indices.map((i) => (
          <Animal key={i} isRabbit={isRabbitAt(i, heads, rabbits)} step={step} />
        ))}
      </div>
    </div>
  );
}

function ChickenRabbitExplainer({ heads, legs, onDone, ageBand }) {
  const [step, setStep] = useState(0);
  const answer = solveChickenRabbit(heads, legs);

  if (!answer) {
    return (
      <p className="text-center text-base text-gray-600">
        这组数字凑不成一道鸡兔同笼题:脚数要是双数,并且在头数的 2 倍到 4 倍之间。
      </p>
    );
  }

  const { rabbits, chickens } = answer;
  const afterFirst = legs - heads;
  const afterSecond = legs - 2 * heads;

  // 每一步的说明;只显示当前这一步
  const explanations = [
    <>
      笼子里有 <b>{heads}</b> 只小动物、<b>{legs}</b> 只脚。谁是鸡🐔,谁是兔🐰?
    </>,
    <>
      每只都迈出一条腿,出去了 {heads} 条。笼子里还剩 <b>{afterFirst}</b> 只脚。
    </>,
    <>
      鸡只有 2 条腿,已经<b>整只走出笼子</b>啦!留在笼子里的,<b>全是兔子</b>,笼里还剩{' '}
      <b>{afterSecond}</b> 只脚。
    </>,
    <>
      每只兔子笼里剩 2 只脚:<b>{rabbits}</b> 只兔子🐰;鸡就是 {heads} − {rabbits} ={' '}
      <b>{chickens}</b> 只🐔。
    </>,
  ];

  // 到目前为止算过的式子,缩成一排小标签,保留推导链
  const equations = [
    `${legs} − ${heads} = ${afterFirst}`,
    `${afterFirst} − ${heads} = ${afterSecond}`,
    `${afterSecond} ÷ 2 = ${rabbits}`,
  ].slice(0, step);

  const tiers = [];
  for (let start = 0; start < heads; start += PER_TIER) {
    tiers.push(
      Array.from({ length: Math.min(PER_TIER, heads - start) }, (_, k) => start + k),
    );
  }

  return (
    <div className="flex w-full flex-col items-center gap-3">
      <div className="flex w-full flex-col gap-1">
        <div className="h-2 w-full rounded-t bg-amber-900" />
        {tiers.map((indices) => (
          <CageTier
            key={indices[0]}
            indices={indices}
            heads={heads}
            rabbits={rabbits}
            step={step}
          />
        ))}
      </div>

      <div className="flex min-h-[1.75rem] flex-wrap justify-center gap-1.5">
        {equations.map((eq) => (
          <span
            key={eq}
            data-testid="equation"
            className="rounded-full border border-gray-400 bg-white px-2.5 py-0.5 text-sm text-gray-700"
          >
            {eq}
          </span>
        ))}
      </div>

      <p
        data-testid="explanation"
        className="min-h-[4.5rem] w-full rounded-lg bg-qiji-gold px-3 py-2 text-base leading-relaxed text-gray-900"
      >
        {explanations[step]}
        {step === LAST_STEP && (
          <span className="mt-1 block text-sm text-gray-700">
            验算:{chickens} × 2 + {rabbits} × 4 = {legs} 只脚 ✓
          </span>
        )}
      </p>

      <div className="flex items-center gap-3">
        {/* 进度点 */}
        <div className="flex gap-1" aria-hidden="true">
          {Array.from({ length: LAST_STEP + 1 }, (_, i) => (
            <span
              key={i}
              className={`h-2 w-2 rounded-full ${i <= step ? 'bg-gray-900' : 'bg-gray-300'}`}
            />
          ))}
        </div>

        {step < LAST_STEP ? (
          <button
            type="button"
            onClick={() => setStep(step + 1)}
            className="rounded-lg bg-qiji-mint px-5 py-2.5 text-base font-bold text-gray-900
                       border-2 border-black shadow-brutal-sm transition
                       hover:-translate-y-0.5 active:translate-y-0 active:shadow-none"
          >
            {STEP_BUTTONS[step]}
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={() => setStep(0)}
              className="rounded-lg bg-white px-4 py-2.5 text-base font-bold text-gray-900
                         border-2 border-black shadow-brutal-sm transition
                         hover:-translate-y-0.5 active:translate-y-0 active:shadow-none"
            >
              再看一遍
            </button>
            {onDone && (
              <button
                type="button"
                onClick={onDone}
                className="rounded-lg bg-qiji-gold px-5 py-2.5 text-base font-bold text-gray-900
                           border-2 border-black shadow-brutal-sm transition
                           hover:-translate-y-0.5 active:translate-y-0 active:shadow-none"
              >
                {getCopy(ageBand).explainerButton}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default ChickenRabbitExplainer;
